package dev.kuml.portal.web.i18n.strings

/**
 * See this wave's plan §5.8: [trainingConsentLabel]/[trainingConsentHint] and
 * [showcaseConsentLabel]/[showcaseConsentHint] are RECHTLICH WIRKSAME Erklärungstexte, not ordinary
 * UI copy -- they must stay in lockstep, in EACH language, with the corresponding explanation in
 * `dev.kuml.portal.web.i18n.legal.LegalPagesDe`/`LegalPagesEn`'s privacy page, section 6 (same
 * legal bases, same "not granted by default" statement, same withdrawal/objection mechanics). Any
 * change here requires a matching check against that section.
 */
public interface ProfileStrings {
    public val pageTitle: String
    public val displayNameLabel: String
    public val displayNameAriaLabel: String
    public val saveName: String
    public val savingName: String
    public val nameSaved: String
    public val nameSaveFailed: String
    public val changeImage: String
    public val removeImage: String
    public val uploadingImage: String
    public val avatarUpdated: String
    public val avatarRemoved: String
    public val avatarUploadFailed: String
    public val avatarRemoveFailed: String
    public val dataUsageHeading: String
    public val settingSaved: String
    public val settingSaveFailed: String
    public val profileLoadFailed: String
    public val networkSaveError: String

    public val trainingConsentLabel: String
    public val trainingConsentHint: String
    public val showcaseConsentLabel: String
    public val showcaseConsentHint: String

    // ---------- R-5: Social Login ----------

    public val connectedAccountsTitle: String
    public val connectedAccountsIntro: String
    public val connectAction: String
    public val disconnectAction: String

    /** Shown next to a linked provider -- parameterised with the connection's `createdAt` date
     * string, already formatted by the caller via `t.format`. */
    public fun connectedSince(dateLabel: String): String

    public val notConnected: String
    public val linkedSuccess: String
    public val linkTaken: String
    public val linkSlotOccupied: String
    public val lastLoginMethodRefused: String

    public val allPlainStrings: List<String>
        get() =
            listOf(
                pageTitle,
                displayNameLabel,
                displayNameAriaLabel,
                saveName,
                savingName,
                nameSaved,
                nameSaveFailed,
                changeImage,
                removeImage,
                uploadingImage,
                avatarUpdated,
                avatarRemoved,
                avatarUploadFailed,
                avatarRemoveFailed,
                dataUsageHeading,
                settingSaved,
                settingSaveFailed,
                profileLoadFailed,
                networkSaveError,
                trainingConsentLabel,
                trainingConsentHint,
                showcaseConsentLabel,
                showcaseConsentHint,
                connectedAccountsTitle,
                connectedAccountsIntro,
                connectAction,
                disconnectAction,
                notConnected,
                linkedSuccess,
                linkTaken,
                linkSlotOccupied,
                lastLoginMethodRefused,
            )
}

public object ProfileStringsDe : ProfileStrings {
    override val pageTitle = "Profil"
    override val displayNameLabel = "Anzeigename"
    override val displayNameAriaLabel = "Anzeigename"
    override val saveName = "Namen speichern"
    override val savingName = "Anzeigename wird gespeichert …"
    override val nameSaved = "Anzeigename gespeichert."
    override val nameSaveFailed = "Der Anzeigename konnte nicht gespeichert werden."
    override val changeImage = "Bild ändern"
    override val removeImage = "Bild entfernen"
    override val uploadingImage = "Bild wird hochgeladen …"
    override val avatarUpdated = "Profilbild aktualisiert."
    override val avatarRemoved = "Profilbild entfernt."
    override val avatarUploadFailed = "Das Bild konnte nicht hochgeladen werden."
    override val avatarRemoveFailed = "Das Bild konnte nicht entfernt werden."
    override val dataUsageHeading = "Datennutzung"
    override val settingSaved = "Einstellung gespeichert."
    override val settingSaveFailed = "Die Einstellung konnte nicht gespeichert werden."
    override val profileLoadFailed = "Profil konnte nicht geladen werden."
    override val networkSaveError = "Netzwerkfehler beim Speichern."
    override val trainingConsentLabel =
        "Meine Diagramme dürfen zur Korrektur von Renderer-Fehlern und zum Training des " +
            "kUML-eigenen, lokal betriebenen KI-Modells verwendet werden."
    override val trainingConsentHint =
        "Ohne Haken legen Sie Widerspruch nach Art. 21 DSGVO ein. Der Widerspruch wirkt für " +
            "künftige Generierungen und für bereits gespeicherte, noch nicht verwendete Generierungen."
    override val showcaseConsentLabel =
        "Zusätzlich: Meine Diagramme dürfen im Trainingsdatensatz bevorzugt berücksichtigt und " +
            "als öffentliches Beispiel (Showcase, Marketing) verwendet werden."
    override val showcaseConsentHint =
        "Freiwillige Einwilligung. Sie können sie jederzeit mit Wirkung für die Zukunft " +
            "widerrufen, indem Sie den Haken entfernen."

    override val connectedAccountsTitle = "Verknüpfte Konten"
    override val connectedAccountsIntro = "Melden Sie sich zusätzlich mit einem Google- oder GitHub-Konto an."
    override val connectAction = "Verknüpfen"
    override val disconnectAction = "Trennen"

    override fun connectedSince(dateLabel: String): String = "Verknüpft seit $dateLabel"

    override val notConnected = "Nicht verknüpft"
    override val linkedSuccess = "Konto erfolgreich verknüpft."
    override val linkTaken = "Dieses Konto ist bereits mit einem anderen kUML-Konto verknüpft."
    override val linkSlotOccupied = "Für diesen Anbieter ist bereits ein anderes Konto verknüpft. Bitte trennen Sie es zuerst."
    override val lastLoginMethodRefused =
        "Dies ist Ihre einzige Anmeldemethode. Legen Sie zuerst ein Passwort fest oder verknüpfen Sie ein " +
            "weiteres Konto, bevor Sie dieses trennen."
}

public object ProfileStringsEn : ProfileStrings {
    override val pageTitle = "Profile"
    override val displayNameLabel = "Display name"
    override val displayNameAriaLabel = "Display name"
    override val saveName = "Save name"
    override val savingName = "Saving display name …"
    override val nameSaved = "Display name saved."
    override val nameSaveFailed = "The display name could not be saved."
    override val changeImage = "Change image"
    override val removeImage = "Remove image"
    override val uploadingImage = "Uploading image …"
    override val avatarUpdated = "Profile picture updated."
    override val avatarRemoved = "Profile picture removed."
    override val avatarUploadFailed = "The image could not be uploaded."
    override val avatarRemoveFailed = "The image could not be removed."
    override val dataUsageHeading = "Data usage"
    override val settingSaved = "Setting saved."
    override val settingSaveFailed = "The setting could not be saved."
    override val profileLoadFailed = "Profile could not be loaded."
    override val networkSaveError = "Network error while saving."
    override val trainingConsentLabel =
        "My diagrams may be used to fix renderer bugs and to train kUML's own, locally operated " +
            "AI model."
    override val trainingConsentHint =
        "Unchecking this box files an objection under Art. 21 GDPR. The objection applies to " +
            "future generations and to already-stored generations that have not yet been used."
    override val showcaseConsentLabel =
        "In addition: my diagrams may be prioritised in the training dataset and used as a public " +
            "example (showcase, marketing)."
    override val showcaseConsentHint =
        "Voluntary consent. You can withdraw it at any time with effect for the future by " +
            "unchecking the box."

    override val connectedAccountsTitle = "Connected accounts"
    override val connectedAccountsIntro = "Also sign in with a Google or GitHub account."
    override val connectAction = "Connect"
    override val disconnectAction = "Disconnect"

    override fun connectedSince(dateLabel: String): String = "Connected since $dateLabel"

    override val notConnected = "Not connected"
    override val linkedSuccess = "Account connected successfully."
    override val linkTaken = "This account is already connected to a different kUML account."
    override val linkSlotOccupied = "A different account is already connected for this provider. Please disconnect it first."
    override val lastLoginMethodRefused =
        "This is your only sign-in method. Set a password or connect another account before disconnecting this one."
}
